'use client';

/**
 * The applied-filter chips row (BAL-665). Removable pills for every active
 * filter + a Clear-all + a result count. The single biggest usability win of
 * the listing-filter pattern — always render it when any filter is active.
 */
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import type { AppliedChip } from './types';

interface AppliedFilterChipsProps {
  chips: AppliedChip[];
  resultCount?: number;
  onRemove: (chip: AppliedChip) => void;
  onClearAll: () => void;
  className?: string;
}

export function AppliedFilterChips({
  chips,
  resultCount,
  onRemove,
  onClearAll,
  className,
}: AppliedFilterChipsProps) {
  const { t } = useTranslation();

  if (chips.length === 0) {
    return resultCount != null ? (
      <p className={cn('text-muted-foreground text-sm', className)}>
        <Trans
          i18nKey="common:resultsCount"
          values={{ count: resultCount }}
          defaults="{{count}} results"
        />
      </p>
    ) : null;
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {resultCount != null ? (
        <span className="text-muted-foreground mr-1 text-sm">
          <Trans
            i18nKey="common:resultsCount"
            values={{ count: resultCount }}
            defaults="{{count}} results"
          />
        </span>
      ) : null}

      {chips.map((chip) => (
        <Badge
          key={`${chip.facetKey}:${chip.value ?? 'range'}`}
          variant="secondary"
          className="gap-1 pr-1"
        >
          {chip.label}
          {/* eslint-disable-next-line kit-components/use-kit-primitives -- tiny dismiss X nested inside a Badge pill (p-0.5, size-3 icon); Button's smallest size chrome (h-9/w-9) would blow out the badge */}
          <button
            type="button"
            onClick={() => onRemove(chip)}
            className="hover:bg-muted-foreground/20 ml-0.5 rounded-full p-0.5"
            aria-label={t('common:remove', {
              defaultValue: 'Remove {{label}}',
              label: chip.label,
            })}
          >
            <XIcon className="size-3" />
          </button>
        </Badge>
      ))}

      <Button
        variant="ghost"
        size="sm"
        onClick={onClearAll}
        className="text-muted-foreground h-7"
      >
        <Trans i18nKey="common:clearFilters" />
      </Button>
    </div>
  );
}
